import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { PencilSimple, Trash, MagicWand, ClipboardText, LockSimple, LockSimpleOpen, ArrowsClockwise, Eye, EyeSlash, Copy, Plus, DotsSixVertical, CalendarPlus, CheckCircle, WarningCircle, ShieldCheck, CaretDown, FilePdf, Sparkle, Trophy } from "@phosphor-icons/react";
import { Header, AddBtn, Modal, In, Sel, SaveBtn, IconBtn } from "./shared";
import { ScheduleGenerator } from "./ScheduleGenerator";
import { BoxScoreEditor } from "./BoxScoreEditor";
import { PlayoffScheduleTable } from "@/components/PlayoffScheduleTable";
import { PlayoffMatchModal } from "./PlayoffMatchModal";

const STATUS_STYLE = {
  scheduled: "bg-[#FF5722]/20 text-[#FF5722]",
  live: "bg-[#00E676]/20 text-[#00E676]",
  completed: "bg-white/10 text-white/60",
  postponed: "bg-[#FFB300]/20 text-[#FFB300]",
  cancelled: "bg-[#FF3B30]/20 text-[#FF3B30]",
};
const STATUS_LABEL = { scheduled: "Upcoming", live: "Live", completed: "Final", postponed: "Postponed", cancelled: "Cancelled" };
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d - off * 60000).toISOString().slice(0, 16); };

const CheckIssue = ({ show, title, testid, children }) => {
  if (!show) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testid}>
      <WarningCircle size={16} weight="fill" className="text-[#FFB300] shrink-0 mt-0.5" />
      <div><span className="font-semibold text-white/90">{title}:</span> <span className="text-white/60">{children}</span></div>
    </div>
  );
};

export default function ScheduleTab() {
  const { settings } = useSettings();
  const divisions = settings.divisions || ["Open"];
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [division, setDivision] = useState(() => sessionStorage.getItem("admin_sched_division") || (settings.divisions || ["Open"])[0] || "Open");
  const [fSeason, setFSeason] = useState("all");
  const [fWeek, setFWeek] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [boxGame, setBoxGame] = useState(null);
  const [recalcing, setRecalcing] = useState(false);
  const [check, setCheck] = useState(null);
  const [showCheck, setShowCheck] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = (n) => setCollapsed((c) => ({ ...c, [n]: !c[n] }));
  const [loading, setLoading] = useState(false);
  const [playoffBracket, setPlayoffBracket] = useState(null);
  const [showPlayoffs, setShowPlayoffs] = useState(true);
  const [editPlayoffMatch, setEditPlayoffMatch] = useState(null);
  const empty = { home_team_id: "", away_team_id: "", date: "", venue: "", court: "", status: "scheduled", home_score: 0, away_score: 0, week: "" };
  const [form, setForm] = useState(empty);
  const dragRef = useRef(null);

  const load = useCallback(() => {
    api.get("/games").then((r) => setGames(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
    api.get(`/weeks?division=${encodeURIComponent(division)}`).then((r) => setWeeks(r.data));
    api.get(`/schedule/check?division=${encodeURIComponent(division)}`).then((r) => setCheck(r.data)).catch(() => setCheck(null));
    api.get(`/playoffs/${encodeURIComponent(division)}`).then((r) => setPlayoffBracket(r.data.bracket)).catch(() => setPlayoffBracket(null));
  }, [division]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { sessionStorage.setItem("admin_sched_division", division); setFWeek("all"); }, [division]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "TBD";
  const divTeams = teams.filter((t) => (t.division || "Open") === division);
  const divGames = games.filter((g) => (g.division || "Open") === division);
  const seasons = Array.from(new Set(divGames.map((g) => g.season).filter(Boolean)));
  const passFilters = (g) => (fSeason === "all" || g.season === fSeason) && (fStatus === "all" || g.status === fStatus);
  const gamesInWeek = (n) => divGames.filter((g) => g.week === n && passFilters(g)).sort((a, b) => (a.order || 0) - (b.order || 0) || a.date.localeCompare(b.date));
  const unscheduled = divGames.filter((g) => g.week == null && passFilters(g));
  const visibleWeeks = weeks.filter((w) => fWeek === "all" || String(w.number) === String(fWeek));

  // Per-week hide/unhide of completed games. Default: completed games are HIDDEN.
  const [showDone, setShowDone] = useState({}); // week number -> true means show completed
  const weekCompletedCount = (n) => gamesInWeek(n).filter((g) => g.status === "completed").length;
  const weekVisibleGames = (n) => {
    const all = gamesInWeek(n);
    if (showDone[n] || fStatus === "completed") return all; // explicit "Final" filter always shows them
    return all.filter((g) => g.status !== "completed");
  };

  const payloadOf = (g, ov = {}) => ({
    home_team_id: g.home_team_id, away_team_id: g.away_team_id, date: new Date(g.date).toISOString(),
    venue: g.venue || "", court: g.court || "", status: g.status || "scheduled",
    home_score: g.home_score || 0, away_score: g.away_score || 0, season: g.season || "",
    division: g.division || division, week: g.week, order: g.order || 0, ...ov,
  });

  const openNew = (wk) => { setEditing(null); setForm({ ...empty, week: wk ?? "" }); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g, date: toLocalInput(g.date), week: g.week ?? "" }); setOpen(true); };

  const recalc = async () => {
    setRecalcing(true);
    try { const r = await api.post("/players/recalculate"); toast.success(`Recalculated ${r.data.players} players`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRecalcing(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.home_team_id || !form.away_team_id) { toast.error("Select both teams"); return; }
    if (form.home_team_id === form.away_team_id) { toast.error("Teams must differ"); return; }
    const hd = teams.find((t) => t.id === form.home_team_id)?.division || "Open";
    const ad = teams.find((t) => t.id === form.away_team_id)?.division || "Open";
    if (hd !== ad) { toast.error("Both teams must belong to the same division."); return; }
    setLoading(true);
    const payload = { ...form, date: new Date(form.date).toISOString(), division: form.division || division,
      week: form.week === "" ? null : parseInt(form.week, 10),
      home_score: parseInt(form.home_score, 10) || 0, away_score: parseInt(form.away_score, 10) || 0 };
    try {
      if (editing) await api.put(`/games/${editing.id}`, payload);
      else await api.post("/games", payload);
      toast.success("Game saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this game?")) return; try { await api.delete(`/games/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  // --- Week controls ---
  const addWeek = async () => { try { await api.post("/weeks", { division }); toast.success("Week added"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const patchWeek = async (w, body) => { try { await api.put(`/weeks/${w.id}`, body); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const copyWeek = async (w) => { try { const r = await api.post(`/weeks/${w.id}/copy`); toast.success(`Copied to Week ${r.data.number}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const regenWeek = async (w) => { if (!window.confirm(`Regenerate Week ${w.number}? Completed games are kept.`)) return; try { const r = await api.post(`/weeks/${w.id}/regenerate`, { courts: 1 }); toast.success(`Regenerated (${r.data.created} games, ${r.data.kept_completed} kept)`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const deleteWeek = async (w) => { if (!window.confirm(`Delete Week ${w.number} and its non-completed games?`)) return; try { await api.delete(`/weeks/${w.id}`); toast.success("Week deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const autofill = async () => {
    if (!window.confirm(`Auto-fill all missing matchups for ${division} into open weeks?`)) return;
    try { const r = await api.post(`/schedule/autofill?division=${encodeURIComponent(division)}`); toast.success(`Scheduled ${r.data.created} missing matchup(s)`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const downloadSheet = async (w) => {
    try {
      const res = await api.get(`/weeks/${w.id}/sheet.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${division}-week-${w.number}-sheet.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Sheet export failed"); }
  };
  const downloadPlayoffSheet = async () => {
    try {
      const res = await api.get(`/playoffs/${encodeURIComponent(division)}/sheet.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${division}-playoff-schedule.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Playoff sheet export failed"); }
  };

  // --- Drag & drop ---
  const onDropWeek = async (targetWeek) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d) return;
    const g = games.find((x) => x.id === d.gameId);
    if (!g || g.week === targetWeek) return;
    try { await api.put(`/games/${g.id}`, payloadOf(g, { week: targetWeek })); toast.success(`Moved to Week ${targetWeek}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const onDropGame = async (targetGame) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d || d.gameId === targetGame.id) return;
    const src = games.find((x) => x.id === d.gameId);
    if (!src) return;
    if (src.week !== targetGame.week) { onDropWeekSync(src, targetGame.week); return; }
    const list = gamesInWeek(targetGame.week).map((x) => x.id);
    const from = list.indexOf(src.id), to = list.indexOf(targetGame.id);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    try { await api.post("/games/reorder", { ids: list }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const onDropWeekSync = async (g, targetWeek) => {
    try { await api.put(`/games/${g.id}`, payloadOf(g, { week: targetWeek })); toast.success(`Moved to Week ${targetWeek}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const allow = (e) => e.preventDefault();

  const GameRow = ({ g }) => (
    <div draggable onDragStart={() => { dragRef.current = { gameId: g.id, week: g.week }; }}
      onDragOver={allow} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropGame(g); }}
      className="border border-white/10 bg-[#121212] p-3 flex items-center gap-3 flex-wrap cursor-grab active:cursor-grabbing hover:border-white/25 transition-colors" data-testid={`game-row-${g.id}`}>
      <DotsSixVertical size={16} className="text-white/25 shrink-0" />
      <div className="flex-1 min-w-[180px]">
        <div className="font-display text-base uppercase">{teamName(g.home_team_id)} <span className="text-[#FF5722]">vs</span> {teamName(g.away_team_id)}</div>
        <div className="text-white/40 text-xs">{new Date(g.date).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} {g.court && `· ${g.court}`} {g.venue && `· ${g.venue}`}</div>
      </div>
      {g.status === "completed" && <div className="font-display text-xl tabular-nums">{g.home_score}-{g.away_score}</div>}
      {g.status === "completed" && g.locked && <LockSimple size={14} weight="fill" className="text-[#FF3B30]" title="Stats locked" />}
      <span className={`uppercase text-[10px] font-bold px-2 py-1 ${STATUS_STYLE[g.status] || STATUS_STYLE.scheduled}`}>{STATUS_LABEL[g.status] || g.status}</span>
      {g.status === "completed" && <IconBtn onClick={() => setBoxGame(g)} testid={`boxscore-btn-${g.id}`}><ClipboardText size={16} /></IconBtn>}
      <IconBtn onClick={() => openEdit(g)} testid={`edit-game-${g.id}`}><PencilSimple size={16} /></IconBtn>
      <IconBtn onClick={() => del(g.id)} danger testid={`delete-game-${g.id}`}><Trash size={16} /></IconBtn>
    </div>
  );

  const WeekCtl = ({ onClick, active, children, testid, title }) => (
    <button onClick={onClick} data-testid={testid} title={title} className={`p-2 border transition-all duration-200 ${active ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/50 hover:text-white hover:border-white/40"}`}>{children}</button>
  );

  return (
    <div data-testid="schedule-tab">
      <Header title="Schedule" action={
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={recalc} disabled={recalcing} data-testid="recalc-stats-btn" className="flex items-center gap-2 border border-white/15 hover:border-white text-white/70 hover:text-white disabled:opacity-50 font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200 text-sm">
            <ArrowsClockwise size={18} className={recalcing ? "animate-spin" : ""} /> {recalcing ? "…" : "Recalc"}
          </button>
          <button onClick={() => setGenOpen(true)} data-testid="auto-generate-btn" className="flex items-center gap-2 border border-[#FF5722] text-[#FF5722] hover:bg-[#FF5722]/10 font-bold uppercase tracking-wide px-5 py-2.5 transition-all duration-200 text-sm">
            <MagicWand size={18} weight="bold" /> Generate by Week
          </button>
          <AddBtn onClick={() => openNew()} label="Add Game" testid="add-game-btn" />
        </div>
      } />

      <div className="mb-2 flex items-center gap-3">
        <span className="w-2.5 h-8 bg-[#FF5722]" />
        <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none" data-testid="division-heading">{division} Division Schedule</h2>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap bg-[#121212] border border-white/10 p-3">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Division <span className="text-[#FF5722]">*</span></label>
          <select value={division} onChange={(e) => setDivision(e.target.value)} data-testid="division-select" className="bg-[#1A1A1A] border border-[#FF5722]/40 px-3 py-2 text-sm font-bold">
            {divisions.map((d) => <option key={d} value={d}>{`${d} Division`}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Season</label>
          <select value={fSeason} onChange={(e) => setFSeason(e.target.value)} data-testid="filter-season" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
            <option value="all">All Seasons</option>
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Week</label>
          <select value={fWeek} onChange={(e) => setFWeek(e.target.value)} data-testid="filter-week" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
            <option value="all">All Weeks</option>
            {weeks.map((w) => <option key={w.id} value={w.number}>{`Week ${w.number}`}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Status</label>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
            <option value="all">All Statuses</option>
            <option value="scheduled">Upcoming</option>
            <option value="live">Live</option>
            <option value="completed">Final</option>
            <option value="postponed">Postponed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button onClick={addWeek} data-testid="add-week-btn" className="flex items-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 font-bold uppercase tracking-wide px-4 py-2 text-sm transition-all duration-200 self-end">
          <CalendarPlus size={16} /> Add Week
        </button>
        <span className="text-white/40 text-xs self-end">Drag games to reorder or move between weeks</span>
      </div>

      {check && (
        <div className="mb-6 border border-white/10 bg-[#121212]" data-testid="schedule-checker">
          <button onClick={() => setShowCheck((s) => !s)} className="w-full flex items-center gap-3 px-4 py-3 border-b border-white/10 text-left" data-testid="checker-toggle">
            {check.issue_count === 0
              ? <ShieldCheck size={22} weight="fill" className="text-[#00E676]" />
              : <WarningCircle size={22} weight="fill" className="text-[#FFB300]" />}
            <div className="flex-1">
              <div className="font-display text-xl uppercase leading-none">Schedule Checker</div>
              <div className="text-white/40 text-xs mt-0.5" data-testid="checker-summary">
                {check.team_count} teams · {check.total_games} games · {check.completion_pct}% complete ·
                {check.complete ? " season complete" : " season incomplete"} ·
                {check.balanced ? " balanced" : " unbalanced"}
              </div>
            </div>
            <span className={`text-xs font-bold uppercase px-2 py-1 ${check.issue_count === 0 ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#FFB300]/15 text-[#FFB300]"}`} data-testid="checker-issue-count">
              {check.issue_count === 0 ? "All clear" : `${check.issue_count} issue${check.issue_count === 1 ? "" : "s"}`}
            </span>
            <CaretDown size={18} className={`text-white/40 transition-transform ${showCheck ? "rotate-180" : ""}`} />
          </button>

          {showCheck && (
            <div className="p-4 space-y-4">
              {/* Season stats overview */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="checker-stats">
                {[
                  ["Teams", check.team_count],
                  ["Total Games", check.total_games],
                  ["Completed", check.games_completed ?? 0],
                  ["Remaining", check.games_remaining ?? 0],
                  ["Complete", `${check.completion_pct ?? 0}%`],
                  ["Playoff Teams", check.playoff_teams ?? "—"],
                ].map(([label, val]) => (
                  <div key={label} className="border border-white/10 bg-[#0F0F0F] px-3 py-2 text-center">
                    <div className="font-display text-2xl tabular-nums leading-none">{val}</div>
                    <div className="text-[10px] uppercase tracking-wide text-white/40 mt-1">{label}</div>
                  </div>
                ))}
              </div>
              {/* Completion progress bar */}
              <div className="h-2 bg-white/10 overflow-hidden" data-testid="checker-progress">
                <div className="h-full bg-[#00E676] transition-all duration-500" style={{ width: `${check.completion_pct ?? 0}%` }} />
              </div>
              {/* Per-team games overview */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[420px]" data-testid="checker-team-table">
                  <thead>
                    <tr className="text-white/50 uppercase text-[11px] tracking-wide border-b border-white/10">
                      <th className="text-left py-2">Team</th>
                      <th className="py-2 text-center">Scheduled</th>
                      <th className="py-2 text-center">Played</th>
                      <th className="py-2 text-center">Upcoming</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.teams.map((t) => {
                      const low = check.issues.unbalanced.some((u) => u.team === t.name) || t.scheduled === 0;
                      return (
                        <tr key={t.id} className="border-b border-white/5" data-testid={`checker-team-${t.id}`}>
                          <td className="py-1.5 uppercase font-semibold">{t.name}</td>
                          <td className={`py-1.5 text-center tabular-nums ${low ? "text-[#FFB300] font-bold" : ""}`}>{t.scheduled}</td>
                          <td className="py-1.5 text-center tabular-nums">{t.played}</td>
                          <td className="py-1.5 text-center tabular-nums">{t.upcoming}</td>
                        </tr>
                      );
                    })}
                    {check.teams.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-white/30">No teams in this division.</td></tr>}
                  </tbody>
                </table>
              </div>

              {/* Issues */}
              {check.issue_count === 0 ? (
                <div className="flex items-center gap-2 text-[#00E676] text-sm" data-testid="checker-all-clear"><CheckCircle size={18} weight="fill" /> No scheduling problems detected — every team has a complete, balanced schedule.</div>
              ) : (
                <div className="space-y-2.5" data-testid="checker-issues">
                  <CheckIssue show={check.issues.no_games.length} testid="issue-no-games" title="Teams with no games">
                    {check.issues.no_games.join(", ")}
                  </CheckIssue>
                  <CheckIssue show={check.issues.unbalanced.length} testid="issue-unbalanced" title="Teams playing fewer games">
                    {check.issues.unbalanced.map((u) => `${u.team} (${u.scheduled}/${u.max})`).join(", ")}
                  </CheckIssue>
                  <CheckIssue show={check.issues.missing_matchups.length} testid="issue-missing" title="Missing matchups (never scheduled)">
                    {check.issues.missing_matchups.join(" · ")}
                  </CheckIssue>
                  {check.issues.missing_matchups.length > 0 && (
                    <button onClick={autofill} data-testid="autofill-btn" className="flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide text-xs px-4 py-2 mt-1 transition-all duration-200">
                      <Sparkle size={15} weight="fill" /> Auto-Fill {check.issues.missing_matchups.length} Missing Matchup{check.issues.missing_matchups.length === 1 ? "" : "s"}
                    </button>
                  )}
                  <CheckIssue show={check.issues.duplicate_matchups.length} testid="issue-duplicate" title="Duplicate matchups (fine for double round robin)">
                    {check.issues.duplicate_matchups.map((d) => `${d.teams} ×${d.count}`).join(" · ")}
                  </CheckIssue>
                  <CheckIssue show={check.issues.same_day.length} testid="issue-same-day" title="Teams booked twice a day (conflict unless doubleheader)">
                    {check.issues.same_day.map((s) => `${s.team} on ${s.date} (×${s.count})`).join(" · ")}
                  </CheckIssue>
                  <CheckIssue show={check.issues.court_conflicts?.length} testid="issue-court" title="Court double-booked (same court & time)">
                    {(check.issues.court_conflicts || []).map((c) => `${c.court} @ ${new Date(c.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} (×${c.count})`).join(" · ")} — move one game to another court or time.
                  </CheckIssue>
                  <CheckIssue show={check.issues.time_conflicts?.length} testid="issue-time" title="Team booked twice at the same time">
                    {(check.issues.time_conflicts || []).map((c) => `${c.team} @ ${new Date(c.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`).join(" · ")} — reschedule one game.
                  </CheckIssue>
                  <CheckIssue show={check.issues.empty_weeks.length} testid="issue-empty-weeks" title="Weeks with no games">
                    {check.issues.empty_weeks.map((w) => `Week ${w}`).join(", ")} — add games or delete the empty week.
                  </CheckIssue>
                </div>
              )}

              {/* Informational: bye weeks */}
              {check.byes?.length > 0 && (
                <div className="text-xs text-white/50 border-t border-white/10 pt-3" data-testid="checker-byes">
                  <span className="uppercase tracking-wide text-white/40 font-bold">Bye weeks:</span>{" "}
                  {check.byes.map((b) => `Week ${b.week} — ${b.teams.join(", ")}`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {playoffBracket && (
        <div className="mb-6 border-2 border-[#FF5722]/40 bg-[#0D0D0D]" data-testid="admin-playoff-schedule">
          <button onClick={() => setShowPlayoffs((s) => !s)} className="w-full flex items-center gap-3 px-4 py-3 border-b border-white/10 text-left" data-testid="admin-playoff-schedule-toggle">
            <Trophy size={22} weight="fill" className="text-[#FF5722]" />
            <div className="flex-1">
              <div className="font-display text-xl uppercase leading-none">Playoff Schedule</div>
              <div className="text-white/40 text-xs mt-0.5">Linked to the bracket & Scorekeeper — dates, courts and scores stay in sync automatically.</div>
            </div>
            <CaretDown size={18} className={`text-white/40 transition-transform ${showPlayoffs ? "rotate-180" : ""}`} />
          </button>
          {showPlayoffs && (
            <div className="p-4">
              <div className="flex justify-end mb-3">
                <button onClick={downloadPlayoffSheet} data-testid="playoff-sheet-btn" className="flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-white/40 font-bold uppercase tracking-wide px-3 py-2 text-xs transition-all duration-200" title="Printable playoff schedule sheet (PDF)">
                  <FilePdf size={15} /> Printable Playoff Sheet
                </button>
              </div>
              <PlayoffScheduleTable bracket={playoffBracket} onManage={setEditPlayoffMatch} />
              <p className="text-white/30 text-xs mt-3">Tap the pencil on any game to set its date, time & court, send it to the Scorekeeper, enter a result, cancel it, or fix the teams. Seeding and bracket resets live in the Playoffs tab.</p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-5">
        {visibleWeeks.map((w) => (
          <div key={w.id} onDragOver={allow} onDrop={(e) => { e.preventDefault(); onDropWeek(w.number); }} className="border border-white/10" data-testid={`week-card-${w.number}`}>
            <div className="flex items-center gap-3 flex-wrap bg-[#161616] px-4 py-3 border-b border-white/10">
              <button onClick={() => toggleCollapse(w.number)} data-testid={`week-collapse-${w.number}`} title={collapsed[w.number] ? "Expand week" : "Collapse week"} className="text-white/50 hover:text-[#FF5722] transition-colors shrink-0">
                <CaretDown size={20} className={`transition-transform duration-200 ${collapsed[w.number] ? "-rotate-90" : ""}`} />
              </button>
              <button onClick={() => toggleCollapse(w.number)} className="text-left">
                <div className="font-display text-2xl uppercase leading-none">Week {w.number}</div>
                <div className="text-white/40 text-xs mt-0.5">{w.start_date || "—"}{w.end_date && w.end_date !== w.start_date ? ` → ${w.end_date}` : ""} · {w.game_count} games</div>
              </button>
              {w.published ? <span className="text-[10px] uppercase font-bold px-2 py-1 bg-[#00E676]/15 text-[#00E676]">Published</span> : <span className="text-[10px] uppercase font-bold px-2 py-1 bg-white/10 text-white/50">Draft</span>}
              {w.locked && <span className="text-[10px] uppercase font-bold px-2 py-1 bg-[#FF3B30]/15 text-[#FF3B30]">Locked</span>}
              {weekCompletedCount(w.number) > 0 && fStatus !== "completed" && (
                <button onClick={() => setShowDone((s) => ({ ...s, [w.number]: !s[w.number] }))} data-testid={`week-toggle-completed-${w.number}`}
                  title={showDone[w.number] ? "Hide completed games" : "Show completed games"}
                  className={`flex items-center gap-1.5 text-[10px] uppercase font-bold px-2 py-1 border transition-colors ${showDone[w.number] ? "border-[#FF5722] text-[#FF5722]" : "border-white/20 text-white/60 hover:text-white"}`}>
                  {showDone[w.number] ? <><EyeSlash size={13} /> Hide Completed</> : <><Eye size={13} /> Show {weekCompletedCount(w.number)} Completed</>}
                </button>
              )}
              <div className="flex items-center gap-1.5 ml-auto">
                <WeekCtl onClick={() => patchWeek(w, { published: !w.published })} active={w.published} testid={`week-publish-${w.number}`} title={w.published ? "Unpublish" : "Publish"}>{w.published ? <Eye size={16} /> : <EyeSlash size={16} />}</WeekCtl>
                <WeekCtl onClick={() => patchWeek(w, { locked: !w.locked })} active={w.locked} testid={`week-lock-${w.number}`} title={w.locked ? "Unlock" : "Lock"}>{w.locked ? <LockSimple size={16} /> : <LockSimpleOpen size={16} />}</WeekCtl>
                <WeekCtl onClick={() => copyWeek(w)} testid={`week-copy-${w.number}`} title="Copy week"><Copy size={16} /></WeekCtl>
                <WeekCtl onClick={() => downloadSheet(w)} testid={`week-sheet-${w.number}`} title="Printable game sheet (PDF)"><FilePdf size={16} /></WeekCtl>
                <WeekCtl onClick={() => regenWeek(w)} testid={`week-regen-${w.number}`} title="Regenerate week"><ArrowsClockwise size={16} /></WeekCtl>
                <WeekCtl onClick={() => openNew(w.number)} testid={`week-addgame-${w.number}`} title="Add game to week"><Plus size={16} /></WeekCtl>
                <WeekCtl onClick={() => deleteWeek(w)} testid={`week-delete-${w.number}`} title="Delete week"><Trash size={16} /></WeekCtl>
              </div>
            </div>
            <div className="p-3 space-y-2 min-h-[70px]" hidden={!!collapsed[w.number]}>
              {weekVisibleGames(w.number).length === 0
                ? (gamesInWeek(w.number).length > 0 && weekCompletedCount(w.number) > 0
                    ? <p className="text-white/30 text-sm text-center py-4" data-testid={`week-all-completed-${w.number}`}>All {weekCompletedCount(w.number)} game{weekCompletedCount(w.number) === 1 ? "" : "s"} completed — hidden. Use "Show Completed" above.</p>
                    : <p className="text-white/25 text-sm text-center py-4">No games. Drag a game here or use +.</p>)
                : weekVisibleGames(w.number).map((g) => <GameRow key={g.id} g={g} />)}
            </div>
          </div>
        ))}

        {unscheduled.length > 0 && (
          <div className="border border-dashed border-white/15" data-testid="week-unscheduled">
            <div className="bg-[#161616] px-4 py-3 border-b border-white/10 font-display text-xl uppercase text-white/60">Unscheduled</div>
            <div className="p-3 space-y-2">{unscheduled.map((g) => <GameRow key={g.id} g={g} />)}</div>
          </div>
        )}

        {visibleWeeks.length === 0 && unscheduled.length === 0 && <p className="text-white/40">No weeks match your filters. Use "Generate by Week" or "Add Week".</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Game" : `Add Game — ${division} Division`}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Home Team" value={form.home_team_id} onChange={(e) => setForm({ ...form, home_team_id: e.target.value })} data-testid="home-team-select">
              <option value="">Select</option>
              {divTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Sel>
            <Sel label="Away Team" value={form.away_team_id} onChange={(e) => setForm({ ...form, away_team_id: e.target.value })} data-testid="away-team-select">
              <option value="">Select</option>
              {divTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Sel>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label="Date & Time" type="datetime-local" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="game-date-input" />
            <In label="Week #" type="number" value={form.week} onChange={(e) => setForm({ ...form, week: e.target.value })} data-testid="game-week-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label="Court / Gym" value={form.court || ""} onChange={(e) => setForm({ ...form, court: e.target.value })} data-testid="game-court-input" />
            <In label="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
          </div>
          <Sel label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="game-status-select">
            <option value="scheduled">Upcoming</option>
            <option value="live">Live</option>
            <option value="completed">Final</option>
            <option value="postponed">Postponed</option>
            <option value="cancelled">Cancelled</option>
          </Sel>
          {form.status === "completed" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="Home Score" type="number" value={form.home_score} onChange={(e) => setForm({ ...form, home_score: e.target.value })} data-testid="home-score-input" />
              <In label="Away Score" type="number" value={form.away_score} onChange={(e) => setForm({ ...form, away_score: e.target.value })} data-testid="away-score-input" />
            </div>
          )}
          <SaveBtn loading={loading} testid="save-game-btn" />
        </form>
      </Modal>

      {genOpen && <ScheduleGenerator teams={teams} onClose={() => setGenOpen(false)} onSaved={() => { setGenOpen(false); load(); }} />}
      {boxGame && <BoxScoreEditor game={boxGame} teams={teams} onClose={() => { setBoxGame(null); load(); }} onSaved={() => { setBoxGame(null); load(); }} />}
      {editPlayoffMatch && (
        <PlayoffMatchModal
          division={division}
          match={editPlayoffMatch}
          bracket={playoffBracket}
          onClose={() => setEditPlayoffMatch(null)}
          onSaved={(b) => {
            setPlayoffBracket(b);
            setEditPlayoffMatch((prev) => {
              if (!prev) return null;
              const rd = (b.rounds || []).find((r) => r.matches.some((m) => m.id === prev.id));
              const m = rd?.matches.find((x) => x.id === prev.id);
              return m ? { ...m, round: rd.round } : null;
            });
            load();
          }}
        />
      )}
    </div>
  );
}
